import { create } from 'zustand';
import { captureOrgPlanningReviewScope } from '../hooks/useOrgPlanningReviewScope';
import { sameOrgPlanningReviewScope, type OrgPlanningReviewScope } from '../hooks/orgPlanningReviewKey';

export interface AtlasChangesInteraction {
  readonly scope: OrgPlanningReviewScope;
  readonly headChangeSetId: string;
  readonly open: boolean;
  readonly token: number;
  readonly busy?: boolean;
  readonly settled?: boolean;
  readonly blocksEditing?: boolean;
  readonly preparationOutcomeUnknown?: boolean;
}

interface AtlasChangesState {
  readonly interactions: Readonly<Record<string, AtlasChangesInteraction | undefined>>;
  readonly sequence: number;
  openReview: (scope: OrgPlanningReviewScope, headChangeSetId: string) => void;
  close: (token: number) => void;
  replace: (token: number, headChangeSetId: string) => void;
  setBusy: (token: number, busy: boolean) => void;
  blockEditing: (token: number) => void;
  failPreparation: (token: number, headChangeSetId: string, outcomeUnknown: boolean) => void;
  settle: (token: number, headChangeSetId: string) => void;
  clear: () => void;
}

export function atlasChangesContext(folderId: string, diagramId: string): string {
  return JSON.stringify([folderId, diagramId]);
}

/** UI identity only. Reviews, receipts, and authorization remain in Query/server state. */
export const useAtlasChangesStore = create<AtlasChangesState>((set) => ({
  interactions: {},
  sequence: 0,
  openReview: (scope, headChangeSetId) => set((state) => {
    const key = atlasChangesContext(scope.folderId, scope.diagramId);
    const current = state.interactions[key];
    if (current && sameOrgPlanningReviewScope(current.scope, scope)
      && current.headChangeSetId === headChangeSetId) {
      if (current.settled) return state;
      return { interactions: { ...state.interactions, [key]: { ...current, open: true } } };
    }
    if ((current?.open || current?.busy || current?.blocksEditing && !current.settled) && sameOrgPlanningReviewScope(current.scope, scope)) {
      // An unrelated launcher cannot replace the review the user is reading.
      return state;
    }
    const token = state.sequence + 1;
    return { sequence: token, interactions: { ...state.interactions,
      [key]: { scope, headChangeSetId, token, open: true } } };
  }),
  close: (token) => set((state) => {
    const entry = Object.entries(state.interactions).find(([, value]) => value?.token === token);
    if (!entry?.[1]) return state;
    return { interactions: { ...state.interactions, [entry[0]]: { ...entry[1], open: false } } };
  }),
  setBusy: (token, busy) => set((state) => {
    const entry = Object.entries(state.interactions).find(([, value]) => value?.token === token);
    if (!entry?.[1] || entry[1].settled || entry[1].busy === busy) return state;
    return { interactions: { ...state.interactions, [entry[0]]: { ...entry[1], busy } } };
  }),
  blockEditing: (token) => set((state) => {
    const entry = Object.entries(state.interactions).find(([, value]) => value?.token === token);
    if (!entry?.[1] || entry[1].settled || entry[1].blocksEditing) return state;
    return { interactions: { ...state.interactions, [entry[0]]: { ...entry[1], blocksEditing: true } } };
  }),
  failPreparation: (token, headChangeSetId, outcomeUnknown) => set((state) => {
    const entry = Object.entries(state.interactions).find(([, value]) => value?.token === token);
    if (!entry?.[1] || entry[1].settled || entry[1].headChangeSetId !== headChangeSetId) return state;
    // A rejected retry cannot disprove an earlier lost success response.
    // Only an acknowledged transfer may replace that uncertain source head.
    const unknown = outcomeUnknown || entry[1].preparationOutcomeUnknown === true;
    return { interactions: { ...state.interactions, [entry[0]]: {
      ...entry[1], busy: unknown, blocksEditing: unknown, preparationOutcomeUnknown: unknown,
    } } };
  }),
  settle: (token, headChangeSetId) => set((state) => {
    const entry = Object.entries(state.interactions).find(([, value]) => value?.token === token);
    if (!entry?.[1] || entry[1].headChangeSetId !== headChangeSetId) return state;
    return { interactions: { ...state.interactions, [entry[0]]: { ...entry[1], open: false, busy: false, settled: true } } };
  }),
  replace: (token, headChangeSetId) => set((state) => {
    const entry = Object.entries(state.interactions).find(([, value]) => value?.token === token);
    if (!entry?.[1] || entry[1].settled) return state;
    return { interactions: { ...state.interactions, [entry[0]]: { ...entry[1], headChangeSetId, settled: false, preparationOutcomeUnknown: false } } };
  }),
  clear: () => set({ interactions: {} }),
}));

export function openAtlasChanges(folderId: string, diagramId: string, headChangeSetId: string): void {
  useAtlasChangesStore.getState().openReview(captureOrgPlanningReviewScope(folderId, diagramId), headChangeSetId);
}

/** A closed preparation still owns its intent until the server returns an
 * editable successor or a terminal receipt. Never turn it into a fresh draft. */
export function atlasReviewBlocksEditing(scope: OrgPlanningReviewScope): boolean {
  const current = useAtlasChangesStore.getState().interactions[atlasChangesContext(scope.folderId, scope.diagramId)];
  return Boolean(current && !current.settled
    && (current.open || current.busy || current.blocksEditing)
    && sameOrgPlanningReviewScope(current.scope, scope));
}
